/**
 * Navigation Menu Utilities
 * =========================
 *
 * Shared utilities for mobile and desktop navigation menus.
 * Extracted common scroll indicator logic and constants to reduce duplication.
 *
 * @since 2.0.0
 */

// Breakpoint values are defined in breakpoints.js — the single source of truth.
// Re-exported here so existing menu-file imports continue to work unchanged.
export { BREAKPOINTS } from './breakpoints.js'

/**
 * Animation timing constants
 */
export const TIMING = {
  MENU_SLIDE: 300, // Menu slide in/out animation
  ACCORDION_OPEN: 300, // Accordion open animation
  INDICATOR_DELAY: 350 // Delay before showing scroll indicator
}

/**
 * Scroll Indicator Utility
 * ========================
 *
 * Manages scroll indicator visibility for scrollable containers.
 * Detects overflow and shows/hides indicator based on scroll position.
 *
 * @example
 * const indicator = new ScrollIndicator(element, 'show-indicator')
 * indicator.attach()
 * // Later...
 * indicator.detach()
 */
export class ScrollIndicator {
  /**
   * Create a scroll indicator manager
   *
   * @param {HTMLElement} element - The scrollable element to monitor
   * @param {string} bodyClass - Class to add to body when indicator should show
   * @param {number} threshold - Distance from bottom (px) to consider "scrolled to bottom"
   */
  constructor (element, bodyClass, threshold = 10) {
    if (!element) {
      throw new Error('ScrollIndicator requires an element')
    }

    this.element = element
    this.bodyClass = bodyClass
    this.threshold = threshold
    this.scrollHandler = null
  }

  /**
   * Check if element is scrolled to bottom
   *
   * @returns {boolean} True if within threshold pixels of bottom
   */
  isScrolledToBottom () {
    const scrollBottom = this.element.scrollHeight - this.element.scrollTop - this.element.clientHeight
    return scrollBottom <= this.threshold
  }

  /**
   * Check if element has vertical overflow
   *
   * @returns {boolean} True if content is taller than container
   */
  hasOverflow () {
    return this.element.scrollHeight > this.element.clientHeight
  }

  /**
   * Update indicator visibility based on scroll state
   */
  update () {
    if (this.hasOverflow() && !this.isScrolledToBottom()) {
      document.body.classList.add(this.bodyClass)
    } else {
      document.body.classList.remove(this.bodyClass)
    }
  }

  /**
   * Attach scroll listener and perform initial check
   */
  attach () {
    this.detach() // Remove existing listener if any

    this.scrollHandler = () => this.update()
    this.element.addEventListener('scroll', this.scrollHandler, { passive: true })

    // Initial check
    this.update()
  }

  /**
   * Remove scroll listener and hide indicator
   */
  detach () {
    if (this.scrollHandler) {
      this.element.removeEventListener('scroll', this.scrollHandler)
      this.scrollHandler = null
    }

    document.body.classList.remove(this.bodyClass)
  }
}

/**
 * Close all accordions in a container
 *
 * @param {HTMLElement} container - Container element with accordions
 */
export function closeAccordions (container) {
  if (!container) return

  const triggers = container.querySelectorAll('[data-accordion-trigger]')
  triggers.forEach(trigger => {
    if (trigger.getAttribute('aria-expanded') === 'true') {
      trigger.setAttribute('aria-expanded', 'false')

      const panelId = trigger.getAttribute('aria-controls')
      const panel = panelId ? document.getElementById(panelId) : null
      if (panel) {
        panel.classList.remove('is-open')
        panel.setAttribute('aria-hidden', 'true')
        panel.setAttribute('inert', '')
      }

      const parentItem = trigger.closest('[data-accordion-item]')
      if (parentItem) {
        parentItem.classList.remove('is-expanded')
      }
    }
  })
}
